import { useMemo } from "react";
import {
  hasPermission,
  hasPermissionByName,
  // getUserPermissions,
  hasAnyPermission,
  hasAllPermissions,
  getUserRoleLevel,
  canAccessSystem,
  SYSTEMS,
  generatePermissionName,
} from "../permissions";
import { useAuthContext } from "../../_helper/Auth";

/**
 * Custom hook for permission checking
 * @returns {Object} Permission checking functions and utilities
 */
export const usePermissions = () => {
  const { user } = useAuthContext();

  const permissionUtils = useMemo(
    () => ({
      /**
       * Check if current user has permission for a specific action on a resource
       * @param {number} systemId - System ID (1-4)
       * @param {string} permissionName - Permission name
       * @param {Object} resource - Resource being acted upon (optional)
       * @param {Object} context - Additional context data (optional)
       * @returns {boolean} Whether user has permission
       */
      hasPermission: (
        systemId,
        permissionName,
        resource = null,
        context = {},
      ) => {
        return hasPermission(
          user?.permissionSet,
          systemId,
          permissionName,
          resource,
          context,
        );
      },

      hasPermissions: (
        permissionNames,
        resource = null,
        context = {},
      ) => {
        return hasAllPermissions(
          user?.permissionSet,
          permissionNames.map((item) => item instanceof String ? item : item.permissionName),
          resource,
          context,
        );
      },

      /**
       * Check if current user has permission using full permission name
       * @param {string} fullPermissionName - Full permission name (e.g., "1_list_users")
       * @param {Object} resource - Resource being acted upon (optional)
       * @param {Object} context - Additional context data (optional)
       * @returns {boolean} Whether user has permission
       */
      hasPermissionByName: (
        fullPermissionName,
        resource = null,
        context = {},
      ) => {
        return hasPermissionByName(
          user?.permissionSet,
          fullPermissionName,
          resource,
          context,
        );
      },

      /**
       * Get all permissions for current user in a specific system
       * @param {number} systemId - System ID
       * @returns {Array} Array of permission names the user has
       */
      // getUserPermissions: (systemId) => {
      //   return getUserPermissions(user?.permissionSet, systemId);
      // },

      /**
       * Check if current user has any of the specified permissions
       * @param {Array} permissionNames - Array of full permission names
       * @param {Object} resource - Resource being acted upon (optional)
       * @param {Object} context - Additional context data (optional)
       * @returns {boolean} Whether user has any of the permissions
       */
      hasAnyPermission: (permissionNames, resource = null, context = {}) => {
        return hasAnyPermission(
          user?.permissionSet,
          permissionNames,
          resource,
          context,
        );
      },

      /**
       * Check if current user has all of the specified permissions
       * @param {Array} permissionNames - Array of full permission names
       * @param {Object} resource - Resource being acted upon (optional)
       * @param {Object} context - Additional context data (optional)
       * @returns {boolean} Whether user has all of the permissions
       */
      hasAllPermissions: (permissionNames, resource = null, context = {}) => {
        return hasAllPermissions(
          user?.permissionSet,
          permissionNames,
          resource,
          context,
        );
      },

      /**
       * Get current user's role hierarchy level
       * @returns {number} Role level (higher = more permissions)
       */
      getUserRoleLevel: () => {
        return getUserRoleLevel(user);
      },

      /**
       * Check if current user can access a system
       * @param {number} systemId - System ID
       * @returns {boolean} Whether user can access the system
       */
      canAccessSystem: (systemId) => {
        return canAccessSystem(user?.permissionSet, systemId);
      },

      /**
       * Check if current user can access MAKKAH system
       * @returns {boolean}
       */
      canAccessMakkah: () => {
        return canAccessSystem(user?.permissionSet, SYSTEMS.MAKKAH.id);
      },

      /**
       * Check if current user can access MADINA system
       * @returns {boolean}
       */
      canAccessMadina: () => {
        return canAccessSystem(user?.permissionSet, SYSTEMS.MADINA.id);
      },

      /**
       * Check if current user can access SEASON_HR system
       * @returns {boolean}
       */
      canAccessSeasonalHR: () => {
        return canAccessSystem(user?.permissionSet, SYSTEMS.SEASON_HR.id);
      },

      /**
       * Check if current user can access USER_MANAGEMENT system
       * @returns {boolean}
       */
      canAccessUserManagement: () => {
        return canAccessSystem(user?.permissionSet, SYSTEMS.USER_MANAGEMENT.id);
      },
    }),
    [user],
  );

  return {
    user,
    ...permissionUtils,
  };
};
export default usePermissions;
